import React from "react";
import { formatDate } from "@/utils/dateUtils";
import { ManagedUser } from "@/services/users";
import LockedControl from "@/components/common/LockedControl";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CircleOff, CirclePlay, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import InlineEdit from "./InlineEdit";
import { validateCompany } from "./fieldRules";
import {
  CARD_BODY,
  CARD_CELL,
  CARD_HEADER,
  CARD_LINE_END,
  CARD_LINE_START,
  CARD_ROW,
  CARD_TABLE,
  TableMessageRow,
  TableSkeleton,
} from "./tableStates";

/**
 * The membership's state as a pill that toggles it, in the look of the underwriting decision
 * pill: state text and colour, and a circle forming its right end cap behind a thin gap,
 * holding the ACTION the press performs (a red "off" to deactivate an active user, a plain
 * "play" to activate an inactive one). The whole pill is one button whose name starts with
 * the visible state ("Active: deactivate a@b.com"); nothing interactive is nested in it.
 * Fixed width so the column never shifts.
 */
const StatusPill: React.FC<{
  email: string;
  active: boolean;
  /** Locked: can't change at all. */
  disabled?: boolean;
  /** Saving: dimmed and inert, but it keeps keyboard focus. */
  busy?: boolean;
  onClick?: () => void;
}> = ({ email, active, disabled = false, busy = false, onClick }) => {
  const interactive = !disabled && !busy;
  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={busy || undefined}
      aria-busy={busy || undefined}
      onClick={() => interactive && onClick?.()}
      aria-label={active ? `Active: deactivate ${email}` : `Inactive: activate ${email}`}
      title={active ? `Deactivate ${email}` : `Activate ${email}`}
      className={cn(
        "group relative h-8 w-32 shrink-0 rounded-full text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed",
        busy && "cursor-wait opacity-60"
      )}
    >
      <span
        className={cn(
          "absolute inset-0 flex items-center rounded-full pl-4 transition-[filter]",
          "[mask-image:radial-gradient(circle_at_calc(100%_-_1rem)_50%,transparent_calc(1rem_+_3px),black_calc(1rem_+_3.5px))]",
          active ? "bg-green-100 text-green-800" : "bg-secondary text-secondary-foreground",
          interactive && "group-hover:brightness-95"
        )}
      >
        {active ? "Active" : "Inactive"}
      </span>
      <span
        aria-hidden
        className={cn(
          "absolute inset-y-0 right-0 grid w-8 place-items-center rounded-full border bg-muted text-foreground transition-colors",
          interactive && "group-hover:bg-border"
        )}
      >
        {active ? (
          <CircleOff className="h-4 w-4 text-destructive" />
        ) : (
          <CirclePlay className="h-4 w-4 text-foreground" />
        )}
      </span>
    </button>
  );
};

interface UsersTableProps {
  users: ManagedUser[];
  loading: boolean;
  /** Load failure text; replaces the rows. */
  error: React.ReactNode;
  /** The Brokers page: a company, editable in place, and Delete for email-only brokers. */
  showCompany: boolean;
  emptyMessage: React.ReactNode;
  busyUserId: number | null;
  onSetActive: (user: ManagedUser, active: boolean) => void;
  onSaveCompany: (user: ManagedUser, companyName: string) => Promise<unknown>;
  onEditError: (message: string) => void;
  onDelete: (user: ManagedUser) => void;
}

/** First and last name; empty when unset (always for email-only brokers). */
const fullName = (user: ManagedUser) => [user.first_name, user.last_name].filter(Boolean).join(" ");

/**
 * Smallest md+ table width with and without the Company column: the fixed columns below plus
 * a 12.5rem floor for Email (Brokers also gain Created from xl). Narrower cards scroll the
 * table inside the card instead of squeezing Email away.
 */
const MIN_TABLE_WIDTH = {
  plain: "md:min-w-[38.5rem]",
  company: "md:min-w-[43rem] xl:min-w-[51rem]",
};

/** Brokers' Created column from md up to xl: hidden, so Delete fits beside the expanded
 * sidebar at 1024px. Phone cards keep the date. */
const CREATED_BELOW_XL = "md:max-xl:hidden";

/** The status pill for the user's membership in the manager's organization. */
const MembershipStatus: React.FC<{
  user: ManagedUser;
  busy: boolean;
  onSetActive: UsersTableProps["onSetActive"];
}> = ({ user, busy, onSetActive }) => {
  if (user.is_active === null) return <span className="text-muted-foreground">—</span>;
  const active = user.is_active;
  return (
    <LockedControl locked={!!user.membership_lock} message={user.membership_lock} inline>
      <StatusPill
        email={user.email}
        active={active}
        busy={busy}
        onClick={() => onSetActive(user, !active)}
      />
    </LockedControl>
  );
};

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  error,
  showCompany,
  emptyMessage,
  busyUserId,
  onSetActive,
  onSaveCompany,
  onEditError,
  onDelete,
}) => {
  const columnCount = showCompany ? 6 : 4;
  if (loading) return <TableSkeleton columns={columnCount} />;

  // A fixed layout from md up: set column widths, Email takes the rest, and every cell
  // truncates, so long names and emails never push Delete out of view. Brokers drop Created
  // below xl to make room for Company and Delete.
  return (
    <Table
      className={cn(
        CARD_TABLE,
        "md:table-fixed",
        MIN_TABLE_WIDTH[showCompany ? "company" : "plain"]
      )}
    >
      <TableHeader className={CARD_HEADER}>
        <TableRow>
          <TableHead className="w-36">Name</TableHead>
          <TableHead>Email</TableHead>
          {showCompany && <TableHead className="w-36">Company</TableHead>}
          <TableHead className="w-36">Status</TableHead>
          <TableHead className={cn("w-32", showCompany && CREATED_BELOW_XL)}>Created</TableHead>
          {showCompany && (
            <TableHead className="w-14 text-right">
              <span className="sr-only">Actions</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody className={CARD_BODY}>
        {error ? (
          <TableMessageRow columns={columnCount} error>
            {error}
          </TableMessageRow>
        ) : users.length === 0 ? (
          <TableMessageRow columns={columnCount}>{emptyMessage}</TableMessageRow>
        ) : (
          users.map((user) => {
            const name = fullName(user);
            return (
              // Phone card lines: name (when set), email | trash (email-only brokers), company,
              // created | pill.
              <TableRow key={user.id} className={CARD_ROW}>
                <TableCell
                  className={cn(CARD_CELL, "min-w-0 font-medium", !name && "max-md:hidden")}
                >
                  <span className="block truncate" title={name || undefined}>
                    {name || "—"}
                  </span>
                </TableCell>
                {/* The email shares its card line only with the email-only broker's trash, and is
                  the card's title when there is no name. */}
                <TableCell
                  className={cn(
                    CARD_CELL,
                    showCompany && !user.has_login && CARD_LINE_START,
                    !name && "max-md:font-medium",
                    "max-md:order-1"
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate" title={user.email}>
                      {user.email}
                    </span>
                    {!user.has_login && (
                      <Badge variant="secondary" className="shrink-0 whitespace-nowrap">
                        Email only
                      </Badge>
                    )}
                  </span>
                </TableCell>
                {showCompany && (
                  <TableCell className={cn(CARD_CELL, "max-md:order-3")}>
                    <InlineEdit
                      value={user.company_name}
                      label={`company of ${user.email}`}
                      field="company_name"
                      validate={(value) => validateCompany(value, true)}
                      onSave={(value) => onSaveCompany(user, value)}
                      onError={onEditError}
                      lock={user.account_lock}
                      className="w-full"
                    >
                      <span className="min-w-0 truncate" title={user.company_name}>
                        {user.company_name || "—"}
                      </span>
                    </InlineEdit>
                  </TableCell>
                )}
                <TableCell
                  className={cn(
                    CARD_CELL,
                    CARD_LINE_END,
                    "max-md:order-5",
                    !user.has_login && "max-md:hidden"
                  )}
                >
                  <MembershipStatus
                    user={user}
                    busy={busyUserId === user.id}
                    onSetActive={onSetActive}
                  />
                </TableCell>
                <TableCell
                  className={cn(
                    CARD_CELL,
                    CARD_LINE_START,
                    "whitespace-nowrap text-muted-foreground max-md:order-4",
                    showCompany && CREATED_BELOW_XL
                  )}
                >
                  <span className="md:hidden">Created </span>
                  {formatDate(user.created_at)}
                </TableCell>
                {showCompany && (
                  <TableCell
                    className={cn(
                      CARD_CELL,
                      CARD_LINE_END,
                      "max-md:order-2 md:text-right",
                      user.has_login && "max-md:hidden"
                    )}
                  >
                    {!user.has_login && (
                      <LockedControl
                        locked={!user.can_delete}
                        message={user.delete_blocked_reason}
                        inline
                        aria-label={`Delete ${user.email}: ${user.delete_blocked_reason}`}
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          disabled={busyUserId === user.id}
                          onClick={() => onDelete(user)}
                          aria-label={`Delete ${user.email}`}
                          title={`Delete ${user.email}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </LockedControl>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
};

export default UsersTable;
